import React, { useState } from 'react';
import { X, Trophy, Calendar, DollarSign, Users, Settings, Image as ImageIcon } from 'lucide-react';
import type { Tournament, TournamentMode, TournamentType, ScoringStrategy } from '../../../types/features';
import { tournamentService } from '../../../features/tournaments/tournamentService';
import { playerService } from '../../../features/player/playerService';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

interface CreateTournamentModalProps {
    onClose: () => void;
    onSuccess: (tournament: Tournament) => void;
}

const PRESET_IMAGES = [
    'https://images.unsplash.com/photo-1518544801976-3e159e50e5bb?w=800&h=400&fit=crop',
    'https://images.unsplash.com/photo-1533228876829-65c94e7b5025?w=800&h=400&fit=crop',
    'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800&h=400&fit=crop'
];

// Add this custom input component at the top level
const CustomDateInput = React.forwardRef<HTMLDivElement, { value?: string; onClick?: () => void }>(
    ({ value, onClick }, ref) => (
        <div
            className="w-full px-3 py-2 border rounded-lg focus-within:ring-2 focus-within:ring-yellow-500 
                focus-within:border-yellow-500 cursor-pointer bg-white flex items-center"
            onClick={onClick}
            ref={ref}
        >
            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
            <span className="text-gray-700">{value}</span>
        </div>
    )
);

export const CreateTournamentModal: React.FC<CreateTournamentModalProps> = ({
    onClose,
    onSuccess
}) => {
    const player = playerService.getProfile();
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        imageUrl: PRESET_IMAGES[0],
        startDate: new Date(),
        endDate: new Date(Date.now() + 24 * 60 * 60 * 1000), // Default to tomorrow
        type: 'public' as TournamentType,
        mode: 'friends_party' as TournamentMode,
        inviteCode: '',
        entryFee: 0,
        prizePool: 0,
        minParticipants: 2,
        maxParticipants: 100,
        scoringStrategy: 'max_multiplier' as ScoringStrategy,
        settings: {
            spinLimit: 0,
            minBet: 0,
            includeBonusSpins: false,
            includePurchasedSpins: true,
            joinDeadlineHours: 24
        },
        prizes: [
            { position: 1, amount: 60, isPercentage: true },
            { position: 2, amount: 30, isPercentage: true },
            { position: 3, amount: 10, isPercentage: true }
        ],
        allowLateJoin: true
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    const validateStep = (currentStep: number) => {
        const newErrors: Record<string, string> = {};

        if (currentStep === 1) {
            if (!formData.name.trim()) newErrors.name = 'Name is required';
            if (!formData.startDate) newErrors.startDate = 'Start date is required';
            if (!formData.endDate) newErrors.endDate = 'End date is required';
            if (new Date(formData.startDate) >= new Date(formData.endDate)) {
                newErrors.endDate = 'End date must be after start date';
            }
        }

        if (currentStep === 2) {
            if (formData.mode === 'influencer_party' && formData.prizePool <= 0) {
                newErrors.prizePool = 'Prize pool must be greater than 0';
            }
            if (formData.mode === 'friends_party' && formData.entryFee <= 0) {
                newErrors.entryFee = 'Entry fee must be greater than 0';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(step)) {
            setStep(step + 1);
        }
    };

    const handleBack = () => {
        setStep(step - 1);
    };

    const handleSubmit = async () => {
        if (!validateStep(step)) return;

        try {
            // Determine if it's a progressive prize pool based on mode and entry fee
            const isPrizePoolProgressive = formData.mode === 'friends_party' && formData.entryFee > 0;

            // Create tournament
            const tournament = await tournamentService.createTournament({
                ...formData,
                isPrizePoolProgressive,
                createdBy: player.id
            });

            onSuccess(tournament);
            onClose();
        } catch (error) {
            console.error('Failed to create tournament:', error);
            setErrors({ submit: 'Failed to create tournament. Please try again.' });
        }
    };

    const renderStep1 = () => (
        <div className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tournament Name
                </label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                    placeholder="Enter tournament name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                </label>
                <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                    rows={3}
                    placeholder="Describe your tournament"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date & Time
                    </label>
                    <DatePicker
                        selected={new Date(formData.startDate)}
                        onChange={(date) => {
                            if (date) {
                                setFormData({
                                    ...formData,
                                    startDate: date,
                                    // If end date is before new start date, update it
                                    endDate: new Date(formData.endDate) < date
                                        ? new Date(date.getTime() + 24 * 60 * 60 * 1000)
                                        : formData.endDate
                                });
                            }
                        }}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="MMM d, yyyy h:mm aa"
                        minDate={new Date()}
                        customInput={<CustomDateInput />}
                        className="w-full"
                    />
                    {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date & Time
                    </label>
                    <DatePicker
                        selected={new Date(formData.endDate)}
                        onChange={(date) => date && setFormData({ ...formData, endDate: date })}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="MMM d, yyyy h:mm aa"
                        minDate={new Date(formData.startDate)}
                        customInput={<CustomDateInput />}
                        className="w-full"
                    />
                    {errors.endDate && <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>}
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tournament Image
                </label>
                <div className="grid grid-cols-3 gap-4">
                    {PRESET_IMAGES.map((image, index) => (
                        <button
                            key={index}
                            onClick={() => setFormData({ ...formData, imageUrl: image })}
                            className={`relative aspect-video rounded-lg overflow-hidden border-2 
                                ${formData.imageUrl === image ? 'border-yellow-500' : 'border-transparent'}`}
                        >
                            <img
                                src={image}
                                alt={`Preset ${index + 1}`}
                                className="w-full h-full object-cover"
                            />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tournament Type
                    </label>
                    <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as TournamentType })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                    >
                        <option value="public">Public</option>
                        <option value="private">Private (Invite Only)</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tournament Mode
                    </label>
                    <select
                        value={formData.mode}
                        onChange={(e) => setFormData({ ...formData, mode: e.target.value as TournamentMode })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                    >
                        <option value="friends_party">Friends Party</option>
                        <option value="influencer_party">Influencer's Party</option>
                    </select>
                </div>
            </div>

            {formData.type === 'private' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Invite Code
                    </label>
                    <input
                        type="text"
                        value={formData.inviteCode}
                        onChange={(e) => setFormData({ ...formData, inviteCode: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                        placeholder="Optional custom invite code"
                    />
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entry Fee
                    </label>
                    <input
                        type="number"
                        value={formData.entryFee}
                        onChange={(e) => setFormData({ ...formData, entryFee: Number(e.target.value) })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                        min="0"
                        step="1"
                    />
                    {errors.entryFee && <p className="text-red-500 text-sm mt-1">{errors.entryFee}</p>}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prize Pool
                    </label>
                    <input
                        type="number"
                        value={formData.prizePool}
                        onChange={(e) => setFormData({ ...formData, prizePool: Number(e.target.value) })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                        min="0"
                        step="1"
                    />
                    {errors.prizePool && <p className="text-red-500 text-sm mt-1">{errors.prizePool}</p>}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Participants
                    </label>
                    <input
                        type="number"
                        value={formData.minParticipants}
                        onChange={(e) => setFormData({ ...formData, minParticipants: Number(e.target.value) })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                        min="2"
                        step="1"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Participants
                    </label>
                    <input
                        type="number"
                        value={formData.maxParticipants}
                        onChange={(e) => setFormData({ ...formData, maxParticipants: Number(e.target.value) })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                        min="2"
                        step="1"
                    />
                </div>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scoring Strategy
                </label>
                <select
                    value={formData.scoringStrategy}
                    onChange={(e) => setFormData({ ...formData, scoringStrategy: e.target.value as ScoringStrategy })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                >
                    <option value="max_multiplier">Max Multiplier</option>
                    <option value="total_multiplier">Total Multiplier</option>
                    <option value="bet_sum">Bet Sum</option>
                    <option value="classic">Classic (Win Amount)</option>
                </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Spin Limit
                    </label>
                    <input
                        type="number"
                        value={formData.settings.spinLimit}
                        onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, spinLimit: Number(e.target.value) }
                        })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                        min="0"
                        step="1"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Bet
                    </label>
                    <input
                        type="number"
                        value={formData.settings.minBet}
                        onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, minBet: Number(e.target.value) }
                        })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                        min="0"
                        step="0.1"
                    />
                </div>
            </div>

            <div className="space-y-3">
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="includeBonusSpins"
                        checked={formData.settings.includeBonusSpins}
                        onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, includeBonusSpins: e.target.checked }
                        })}
                        className="w-4 h-4 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <label htmlFor="includeBonusSpins" className="ml-2 text-sm text-gray-700">
                        Include Bonus Spins
                    </label>
                </div>
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="includePurchasedSpins"
                        checked={formData.settings.includePurchasedSpins}
                        onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, includePurchasedSpins: e.target.checked }
                        })}
                        className="w-4 h-4 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <label htmlFor="includePurchasedSpins" className="ml-2 text-sm text-gray-700">
                        Include Purchased Spins
                    </label>
                </div>
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="allowLateJoin"
                        checked={formData.allowLateJoin}
                        onChange={(e) => setFormData({ ...formData, allowLateJoin: e.target.checked })}
                        className="w-4 h-4 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <label htmlFor="allowLateJoin" className="ml-2 text-sm text-gray-700">
                        Allow Late Join
                    </label>
                </div>
            </div>

            {formData.allowLateJoin && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Join Deadline (hours before end)
                    </label>
                    <input
                        type="number"
                        value={formData.settings.joinDeadlineHours}
                        onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, joinDeadlineHours: Number(e.target.value) }
                        })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                        min="1"
                        max="72"
                        step="1"
                    />
                </div>
            )}
        </div>
    );

    const renderStep4 = () => (
        <div className="space-y-6">
            <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">Prize Distribution</h3>
                <p className="text-sm text-yellow-600">
                    {formData.mode === 'friends_party'
                        ? 'Prizes are set as percentages of the total pool'
                        : 'Prizes are set as fixed amounts'}
                </p>
            </div>

            {formData.prizes.map((prize, index) => (
                <div key={index} className="grid grid-cols-3 gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Position
                        </label>
                        <input
                            type="number"
                            value={prize.position}
                            onChange={(e) => {
                                const newPrizes = [...formData.prizes];
                                newPrizes[index] = { ...prize, position: Number(e.target.value) };
                                setFormData({ ...formData, prizes: newPrizes });
                            }}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                            min="1"
                            step="1"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {formData.mode === 'friends_party' ? 'Percentage' : 'Amount'}
                        </label>
                        <input
                            type="number"
                            value={prize.amount}
                            onChange={(e) => {
                                const newPrizes = [...formData.prizes];
                                newPrizes[index] = { ...prize, amount: Number(e.target.value) };
                                setFormData({ ...formData, prizes: newPrizes });
                            }}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                            min="0"
                            step={formData.mode === 'friends_party' ? "1" : "10"}
                        />
                    </div>
                    <button
                        onClick={() => {
                            const newPrizes = formData.prizes.filter((_, i) => i !== index);
                            setFormData({ ...formData, prizes: newPrizes });
                        }}
                        className="text-red-600 hover:text-red-700 px-3 py-2"
                        disabled={formData.prizes.length <= 1}
                    >
                        Remove
                    </button>
                </div>
            ))}

            <button
                onClick={() => {
                    const lastPrize = formData.prizes[formData.prizes.length - 1];
                    const newPrize = {
                        position: lastPrize.position + 1,
                        amount: formData.mode === 'friends_party' ? 10 : 100,
                        isPercentage: formData.mode === 'friends_party'
                    };
                    setFormData({ ...formData, prizes: [...formData.prizes, newPrize] });
                }}
                className="w-full py-2 text-yellow-600 hover:text-yellow-700 border border-yellow-600 
                    hover:border-yellow-700 rounded-lg transition-colors"
            >
                Add Prize Position
            </button>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 p-4 flex-shrink-0">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <Trophy className="w-6 h-6" />
                            Create Tournament
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        {[1, 2, 3, 4].map((s) => (
                            <div
                                key={s}
                                className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-white' : 'bg-white/30'}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-6">
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                        {step === 4 && renderStep4()}

                        {errors.submit && (
                            <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg">
                                {errors.submit}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t p-4 flex justify-between flex-shrink-0">
                    <button
                        onClick={step === 1 ? onClose : handleBack}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                        {step === 1 ? 'Cancel' : 'Back'}
                    </button>
                    <button
                        onClick={step === 4 ? handleSubmit : handleNext}
                        className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg 
                            transition-colors font-medium"
                    >
                        {step === 4 ? 'Create Tournament' : 'Next'}
                    </button>
                </div>
            </div>
        </div>
    );
}; 